import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react'

// Route-level code splitting (PSI #458 follow-up — landing visitor
// shouldn't download teacher / student / admin / tutoring code
// upfront). Each ``lazy(() => import(...))`` produces a separate chunk
// under V3/frontend/dist/assets/ with a content-addressable filename;
// the browser only fetches the chunk corresponding to the route the
// user navigates to. Pre-fix, all 14 page components were statically
// imported here so the public ``/`` visitor pulled ~1 MB of JS just to
// see the splash. Post-fix, the landing payload drops to roughly the
// MarketingHome + SplashScreen + chrome footprint, with everything
// else fetched on demand.
//
// The split boundary is the Route element. The chrome components
// imported below (PrivacyFooter, CookieNotice, switchers, gates) stay
// eager because they render on every route — lazy-loading them would
// just chase a network round-trip with no real win.
//
// MarketingHome is defined inline later in this file (not under
// ./pages/) — it's the landing route element and stays in the main
// chunk so ``/`` paints without an extra fetch.
//
// Project-391-extension addition: ClassListPage is the new ADR 0040
// chunk 5 ``/teacher`` landing (multi-class picker). Same lazy
// treatment as the other route components — only teachers loading
// the dashboard pay for it, not landing visitors.
const ClassListPage = lazy(() => import('./pages/ClassListPage'))
const TeacherDashboard = lazy(() => import('./pages/TeacherDashboard'))
const ClassMaterialsPage = lazy(() => import('./pages/ClassMaterialsPage'))
const TeacherWarranty = lazy(() => import('./pages/TeacherWarranty'))
const StudentLogin = lazy(() => import('./pages/StudentLogin'))
const StudentNotice = lazy(() => import('./pages/StudentNotice'))
const StudentSetPassword = lazy(() => import('./pages/StudentSetPassword'))
const StudentAccountSettings = lazy(
  () => import('./pages/StudentAccountSettings'),
)
const EducatorAccountSettings = lazy(
  () => import('./pages/EducatorAccountSettings'),
)
const TutoringSession = lazy(() => import('./pages/TutoringSession'))
const SignInPage = lazy(() => import('./pages/SignInPage'))
const SignUpPage = lazy(() => import('./pages/SignUpPage'))
const AdminSignInPage = lazy(() => import('./pages/AdminSignInPage'))
const AdminSignUpPage = lazy(() => import('./pages/AdminSignUpPage'))
const AdminHome = lazy(() => import('./pages/AdminHome'))
const DevQuickstart = lazy(() => import('./pages/DevQuickstart'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfService = lazy(() => import('./pages/TermsOfService'))
// Help / documentation pages (#620). Same route-lazy treatment as the
// rest of the app surfaces — landing visitors don't pay for the help
// chrome until they navigate into ``/teacher/help`` / ``/student/help``
// / ``/admin/help``.
const TeacherHelp = lazy(() => import('./pages/TeacherHelp'))
const StudentHelp = lazy(() => import('./pages/StudentHelp'))
const AdminHelp = lazy(() => import('./pages/AdminHelp'))
const HelpArticle = lazy(() => import('./pages/HelpArticle'))
const AdminContact = lazy(() => import('./pages/AdminContact'))
// #442 — customer-facing ops dashboard. Route-level lazy is an
// acceptance criterion: only admins navigating to /admin/ops pay for
// the chunk.
const AdminOps = lazy(() => import('./pages/AdminOps'))
// #408 Area B — per-module build-phase ladder + selective retry. Same
// route-lazy treatment: only admins drilling into a module's build pay
// for the chunk.
const AdminBuildPhases = lazy(() => import('./pages/AdminBuildPhases'))

import SplashScreen from './components/SplashScreen'
import MarketingExtension from './components/MarketingExtension'
import PrivacyFooter from './components/PrivacyFooter'
import RevocationBanner from './components/RevocationBanner'
import RouteErrorBoundary from './components/RouteErrorBoundary'
import CookieNotice from './components/CookieNotice'
import ProviderSwitcher from './components/ProviderSwitcher'
import LanguageSwitcher from './components/LanguageSwitcher'
import ThemeSwitcher from './components/ThemeSwitcher'
// [site-password] remove SiteGate import when public launch happens
import SiteGate from './components/SiteGate'
// [testing-mode] remove TestingModeGate import when public launch happens
import TestingModeGate from './components/TestingModeGate'
import RequireAdmin from './components/RequireAdmin'
import { isDevTeacherActive } from './api/devToken'
import { ThemeProvider } from './theme'


/**
 * Suspense fallback for the lazy-loaded route chunks above.
 *
 * Visually empty by design — the splash chrome (PrivacyFooter,
 * switchers, RevocationBanner) renders OUTSIDE the Suspense boundary
 * and stays visible while the route chunk loads, so the user doesn't
 * see a layout flash. ``aria-busy`` + ``aria-live="polite"`` make the
 * pending state legible to screen readers without flashing visible
 * text content.
 *
 * The visually-hidden ``Loading page…`` span inside the fallback is
 * what an ``aria-live`` region actually announces — an empty region
 * has no text content to announce, which is what CodeRabbit #468
 * round 1 flagged. ``.sr-only`` is the project's global a11y
 * utility class (declared in ``styles/base.css``) — same class
 * already used by MCQQuestion's hidden radios and the session-
 * chrome a11y helpers.
 *
 * For perf measurement: the fallback only appears during the network
 * fetch + parse of the route chunk. On a warm cache (subsequent
 * navigation) it's effectively instant; on a cold first-route-after-
 * landing it's typically <300ms on broadband. If route chunks ever
 * grow large enough that this becomes visible, switch to a route-
 * skeleton component that mimics the destination layout.
 */
function RouteFallback() {
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      style={{ minHeight: '100vh' }}
    >
      <span className="sr-only">Loading page…</span>
    </div>
  )
}

// Gate the teacher tree behind Clerk. Students don't get a frontend
// gate — the backend checks session tokens on every /api/sessions/*
// call, which is enough.
//
// Dev bypass: when /dev/quickstart has stashed a dev teacher token, we
// skip Clerk's <SignedIn> wrapper entirely (the backend honors the dev
// token via auth/deps.py, gated on DEV_TOOLS=1). Only the initial mount
// is checked — if the user exits the dev session from a banner, they
// navigate away which remounts and re-evaluates.
//
// ADR 0040 chunk 5 — ``/teacher`` lands on ``ClassListPage`` (the
// multi-class picker); ``/teacher/class/:classCode`` lands on
// ``TeacherDashboard`` with the URL-driven class code. The
// single-class teacher path auto-redirects inside ClassListPage so
// the picker UI never flashes for the dominant case. Both routes
// share the same Clerk gate + dev-token bypass.
function TeacherListProtected() {
  if (isDevTeacherActive()) {
    return <ClassListPage />
  }
  return (
    <>
      <SignedIn>
        <ClassListPage />
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  )
}

function TeacherDashboardProtected() {
  if (isDevTeacherActive()) {
    return <TeacherDashboard />
  }
  return (
    <>
      <SignedIn>
        <TeacherDashboard />
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  )
}

// #451 PR-4 — class-level source materials dashboard. Mirrors the
// TeacherDashboard wrap (Clerk SignedIn gate + dev-token bypass) so
// the materials route has the same auth posture as the rest of the
// teacher tree.
function ClassMaterialsProtected() {
  if (isDevTeacherActive()) {
    return <ClassMaterialsPage />
  }
  return (
    <>
      <SignedIn>
        <ClassMaterialsPage />
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  )
}

// PIPA P2 (#128) chunk 3 — teacher warranty acceptance page wrapper.
// Mirrors ``TeacherProtected``'s Clerk wrap + dev-token bypass. The
// page itself fetches /api/me/warranty-status on mount and short-
// circuits to /teacher if the warranty is already accepted, so a
// teacher landing here when they don't need the form sees an
// immediate redirect rather than a stale UI flash.
function TeacherWarrantyProtected() {
  if (isDevTeacherActive()) {
    return <TeacherWarranty />
  }
  return (
    <>
      <SignedIn>
        <TeacherWarranty />
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  )
}

// PIPA P3 (#129) PR 5 — teacher self-service account settings.
// Mirrors ``TeacherProtected``'s Clerk wrap + dev-token bypass so a
// teacher can hit the settings page from either authentication mode.
function TeacherSettingsProtected() {
  if (isDevTeacherActive()) {
    return <EducatorAccountSettings apiRole="teacher" />
  }
  return (
    <>
      <SignedIn>
        <EducatorAccountSettings apiRole="teacher" />
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  )
}

// PIPA P3 (#129) PR 5 — admin self-service account settings.
// Admins hit ``/admin/settings`` (language-agnostic per the existing
// admin-route convention). Now gated by RequireAdmin (auth + admin-role
// check + dev-token bypass) in addition to the backend
// ``get_current_admin`` guard (which remains the real authz).
function AdminSettingsProtected() {
  return (
    <RequireAdmin>
      <EducatorAccountSettings apiRole="admin" />
    </RequireAdmin>
  )
}

// #442 — customer-facing ops dashboard. Same RequireAdmin gate as
// /admin/settings (Clerk auth + org:admin role + dev-token bypass);
// the backend ``get_current_admin`` 403 on /api/admin/ops/summary
// remains the real authz.
function AdminOpsProtected() {
  return (
    <RequireAdmin>
      <AdminOps />
    </RequireAdmin>
  )
}

// #408 Area B — per-module build-phase ladder. Same RequireAdmin gate
// as /admin/settings (Clerk auth + org:admin role + dev-token bypass);
// the backend ``get_current_admin`` 403 + the cross-school containment
// in ``_get_admin_scoped_module`` remain the real authz.
function AdminBuildPhasesProtected() {
  return (
    <RequireAdmin>
      <AdminBuildPhases />
    </RequireAdmin>
  )
}

// ── Help / documentation route protection (#620) ──────────────────
//
// Teacher + admin help routes inherit the same auth posture as the
// rest of their respective trees:
//   - Teacher: Clerk SignedIn gate + dev-token bypass via
//     ``isDevTeacherActive()``. Matches ``TeacherListProtected`` etc.
//   - Admin: Clerk SignedIn gate + dev-token bypass via
//     ``isDevTeacherActive()`` — mirrors ``RequireAdmin``
//     (``AdminSettingsProtected``) and the dev admin dashboard (#1078).
//     The backend ``get_current_admin`` is the real authz gate and only
//     honours dev tokens under ``DEV_TOOLS=1``, so this is dev-only with
//     no prod/audit impact; it keeps the static, bilingual admin help
//     reachable instead of dead-ending a dev admin at Clerk sign-in
//     (which the new admin-dashboard entry-point now makes one click
//     away via the sidebar Help link).
//   - Student help has NO frontend gate, same as ``/student``. The
//     student help articles are static + bilingual; no backend call
//     is made that needs a bearer. If PR 2 ever adds a student-only
//     article that hits the API (e.g. fetches the user's class info),
//     that fetch will already enforce auth at the API boundary —
//     keeping the page itself ungated is consistent with how
//     ``StudentLogin`` (the picker landing) works today.
function TeacherHelpProtected() {
  if (isDevTeacherActive()) {
    return <TeacherHelp />
  }
  return (
    <>
      <SignedIn>
        <TeacherHelp />
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  )
}

function TeacherHelpArticleProtected() {
  if (isDevTeacherActive()) {
    return <HelpArticle role="teacher" />
  }
  return (
    <>
      <SignedIn>
        <HelpArticle role="teacher" />
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  )
}

function AdminHelpProtected() {
  if (isDevTeacherActive()) {
    return <AdminHelp />
  }
  return (
    <>
      <SignedIn>
        <AdminHelp />
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  )
}

function AdminHelpArticleProtected() {
  if (isDevTeacherActive()) {
    return <HelpArticle role="admin" />
  }
  return (
    <>
      <SignedIn>
        <HelpArticle role="admin" />
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  )
}

function AdminContactProtected() {
  return (
    <>
      <SignedIn>
        <AdminContact />
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  )
}

// Selector for every snap target in the splash + marketing surface.
// Kept as a top-level constant so it stays in sync between the CSS
// rules in ``styles/snap.css`` and the keyboard handler below.
//
// Pre-#264 PR B: this listed each marketing section by class name
// (``.mkt-s1`` … ``.mkt-s7``). Under CSS Modules those class names
// are now hashed (``MarketingExtension.module.css``); the data
// attribute ``data-marketing-section="N"`` is the stable hook the
// component exposes for cross-file references. ``snap.css``'s
// scroll-snap-align rule keys on the same attribute. ``.splash``
// stays global per PR A's chrome stub.
const SNAP_TARGETS_SELECTOR = '.splash, [data-marketing-section]'

/**
 * Splash + marketing extension rendered together for the public
 * landing page. The splash holds first-fold focus (eehae logo,
 * tagline, CTAs / login flip) and the marketing content below it
 * reveals on scroll for school admins evaluating the product.
 *
 * Section-snap scrolling: while this route is mounted, ``<html>``
 * carries a ``data-snap="marketing"`` attribute that ``snap.css``
 * keys on to enable ``scroll-snap-type: y proximity``. This makes
 * the splash → marketing-sections sequence "settle" on each
 * section's start instead of leaving the user mid-section. Cleanup
 * on unmount restores plain free-scroll for /teacher, /student,
 * /admin, /privacy, and the rest of the app.
 *
 * The attribute toggle (rather than always-on CSS) is the cleanest
 * way to scope ``scroll-snap-type``, which has to live on the
 * scroll container — for this app that's <html>.
 *
 * Keyboard nav: Arrow Up/Down get a small custom handler because
 * the browser default (~40px scroll per press) doesn't compose
 * with proximity snap — the user has to tap many times to "creep"
 * across the proximity threshold, and then whichever neighbor is
 * closer wins (often opposite of intent). The handler maps one
 * tap to one section using ``scrollIntoView({ behavior: 'smooth' })``,
 * which respects each section's existing ``scroll-margin-top: 80px``
 * for the sticky-nav offset. Page Up / Page Down still use the
 * browser default — they scroll by viewport, which composes fine
 * with proximity snap.
 */
function MarketingHome() {
  useEffect(() => {
    document.documentElement.dataset.snap = 'marketing'

    // Threshold for "this section is currently at the top." After
    // ``scrollIntoView({ block: 'start' })`` lands a marketing section,
    // its ``getBoundingClientRect().top`` is NOT 0 — most sections set
    // ``scroll-margin-top: 80px`` for sticky-nav overlap (see the
    // per-section rules in components/MarketingExtension.module.css;
    // s1 + s2 use different offsets). 100 absorbs the typical 80 plus
    // a little float slack — enough headroom for any per-section
    // variant.
    const CURRENT_TOP_THRESHOLD = 100

    // Track the in-flight scroll target so rapid Arrow taps chain
    // correctly. Without this, a second tap fires before the first
    // smooth-scroll lands, the DOM measurement happens mid-animation,
    // and the recomputed "current" can resolve to either the previous
    // OR the current section depending on animation progress —
    // producing the user-reported "ArrowUp jumps all the way to top"
    // and "rapid taps don't compound" bugs. The ref is treated as
    // authoritative for ~600ms after each tap (covers a smooth-scroll
    // animation); after that, we re-measure from the DOM in case the
    // user used another input method (wheel, anchor click).
    let targetIdx = -1
    let lastTapAt = 0

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
      // Don't hijack arrows when the user is in a form field — Cmd/Ctrl/Alt
      // combos (Mac Home/End equivalents, etc.) are also off-limits;
      // Shift-Arrow is for selection inside text inputs but harmless to
      // skip here too since snap doesn't apply during text editing.
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      if (target?.isContentEditable) return

      const sections = Array.from(
        document.querySelectorAll<HTMLElement>(SNAP_TARGETS_SELECTOR),
      )
      if (sections.length === 0) return

      const now = performance.now()
      const recentTap = now - lastTapAt < 600

      let baseIdx: number
      if (recentTap && targetIdx >= 0) {
        // In a rapid-tap chain — the previous scrollIntoView is still
        // animating. Trust the stored target rather than the mid-
        // animation DOM state, which would mis-resolve the section
        // we're "in" and could skip or reverse direction.
        baseIdx = targetIdx
      } else {
        // First tap, or enough time has passed that the user likely
        // used another scroll method. Recompute from the DOM. "Current"
        // = last section whose top is at-or-near viewport top (within
        // the snap-margin offset). Relies on querySelectorAll returning
        // sections in DOM (top-to-bottom) order — true for the current
        // marketing layout; if a future refactor reorders the
        // ``[data-marketing-section]`` elements in the DOM the
        // early-break would resolve wrong.
        baseIdx = 0
        for (let i = 0; i < sections.length; i++) {
          if (sections[i].getBoundingClientRect().top <= CURRENT_TOP_THRESHOLD)
            baseIdx = i
          else break
        }
      }

      const nextIdx =
        e.key === 'ArrowDown'
          ? Math.min(sections.length - 1, baseIdx + 1)
          : Math.max(0, baseIdx - 1)

      // No movement needed (already at the first / last snap target).
      if (nextIdx === baseIdx) return

      e.preventDefault()
      targetIdx = nextIdx
      lastTapAt = now
      const reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches
      sections[nextIdx].scrollIntoView({
        block: 'start',
        behavior: reducedMotion ? 'auto' : 'smooth',
      })
    }

    window.addEventListener('keydown', onKey)
    return () => {
      delete document.documentElement.dataset.snap
      window.removeEventListener('keydown', onKey)
    }
  }, [])

  return (
    <>
      <SplashScreen />
      <MarketingExtension />
    </>
  )
}

function App() {
  return (
    <ThemeProvider>
      {/* [testing-mode] unwrap TestingModeGate when public launch happens.
          Sits OUTSIDE SiteGate so public visitors never see the site-password
          prompt — they only see the testing notice. */}
      <TestingModeGate>
      {/* [site-password] unwrap SiteGate when public launch happens */}
      <SiteGate>
        {/* RouteErrorBoundary wraps the Suspense so a rejected
            ``lazy()`` import doesn't crash the whole app to a white
            screen. It splits the two failure modes (#1115): a
            stale-chunk-hash 404 after a deploy auto-reloads once to
            pick up the new hashes (guarded against a loop), while a
            genuine render crash shows a real error state instead of a
            misleading "new version" notice. Suspense handles the
            pending state of the same lazy imports; the two boundaries
            are complementary. CodeRabbit #468 round 1.
        */}
        <RouteErrorBoundary>
        {/* Suspense boundary for the route-level lazy chunks declared
            at the top of this file. Fallback is intentionally minimal
            (see RouteFallback) — the global chrome (switchers,
            footer, banners) renders BELOW this block and stays
            painted while a route chunk loads. */}
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Clerk + admin routes — language-agnostic URL scheme.
              Lang is determined by the user's stored preference,
              not a URL prefix. */}
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up/*" element={<SignUpPage />} />
          <Route path="/admin/sign-in/*" element={<AdminSignInPage />} />
          <Route path="/admin/sign-up/*" element={<AdminSignUpPage />} />
          <Route path="/admin" element={<AdminHome />} />
          {/* PIPA P3 (#129) PR 5 — admin self-service account settings. */}
          <Route
            path="/admin/settings"
            element={<AdminSettingsProtected />}
          />
          {/* #442 — customer-facing ops dashboard. Language-agnostic,
              matching the rest of the /admin tree. */}
          <Route path="/admin/ops" element={<AdminOpsProtected />} />
          {/* #408 Area B — per-module build-phase ladder + selective
              retry. Language-agnostic, matching the rest of the /admin
              tree. This is the page the ops-summary
              ``BuildFailureRef.retry_url`` hint links to (see
              services/ops_summary.py). */}
          <Route
            path="/admin/modules/:moduleId/build/phases"
            element={<AdminBuildPhasesProtected />}
          />
          {/* Admin help / documentation (#620). Language-agnostic,
              matching the rest of the /admin tree. The article route
              accepts an optional categoryId + slug; PR 1 routes to
              the ComingSoon placeholder for any slug whose body MD
              isn't yet published. */}
          <Route path="/admin/help" element={<AdminHelpProtected />} />
          {/* Admin contact form (#620 PR 4). Sits at a fixed slug
              under /admin/help/ so the help shell + breadcrumb both
              recognize it as part of the help surface. Route is
              before the category-id catch-all below so "contact"
              doesn't match as a category id. */}
          <Route
            path="/admin/help/contact"
            element={<AdminContactProtected />}
          />
          <Route
            path="/admin/help/:categoryId"
            element={<AdminHelpArticleProtected />}
          />
          <Route
            path="/admin/help/:categoryId/:slug"
            element={<AdminHelpArticleProtected />}
          />

          {/* Dev test-harness entrypoint. Gated server-side on DEV_TOOLS=1;
              if the backend doesn't accept the quickstart POST the page
              surfaces a generic error. */}
          <Route path="/dev/quickstart" element={<DevQuickstart />} />

          {/* Privacy policy — locale-agnostic per PIPA #127. Korean
              primary; ``?lang=en`` flips to the English mirror.
              Outside the locale-prefixed tree on purpose so the URL
              admins reference in DPAs stays stable. */}
          <Route path="/privacy" element={<PrivacyPolicy />} />

          {/* Terms of Service (issue #425 item 2). Korean primary;
              ``?lang=en`` flips to the English mirror. Sibling
              document to the Privacy Policy: ToS is contractual,
              Privacy Policy is the statutory PIPA Art. 30 disclosure.
              Outside the locale-prefixed tree for the same reason —
              the URL stays stable across i18n flips so admins can
              reference it in DPAs / sales materials. */}
          <Route path="/terms" element={<TermsOfService />} />

          {/* Korean (default) — no locale prefix.
              Marketing extension renders below the splash on `/` and
              `/en`. The splash itself stays as the above-the-fold
              experience; scrolling past it reveals the marketing
              flow. The two are rendered as siblings rather than
              composed inside SplashScreen to keep the splash
              component's contract narrow (login state, signed-out
              toast, admin entry — no marketing concerns). */}
          <Route path="/" element={<MarketingHome />} />
          {/* ADR 0040 chunk 5 — ``/teacher`` is the multi-class
              landing (ClassListPage); ``/teacher/class/:classCode``
              is the per-class dashboard. Pre-chunk-5 ``/teacher``
              rendered the dashboard directly off a localStorage
              singleton; the new shape demotes that singleton to a
              Resume hint while keeping the single-class auto-redirect
              behavior (handled inside ClassListPage). */}
          <Route path="/teacher" element={<TeacherListProtected />} />
          <Route
            path="/teacher/class/:classCode"
            element={<TeacherDashboardProtected />}
          />
          {/* #451 PR-4 — class-level source materials. Sibling
              of the per-class dashboard route; the dashboard's
              "Manage materials" CTA navigates here. */}
          <Route
            path="/teacher/class/:classCode/materials"
            element={<ClassMaterialsProtected />}
          />
          <Route
            path="/teacher/settings"
            element={<TeacherSettingsProtected />}
          />
          {/* PIPA P2 (#128) chunk 3 — standalone teacher warranty
              acceptance page. Reached via:
                - the chunk-3 TeacherDashboard mount-time gate when
                  the institution's warranty isn't yet attested,
                - direct navigation by the user. The page itself
                  short-circuits to /teacher if the warranty is
                  already accepted (covers the manual-URL case). */}
          <Route
            path="/teacher/warranty"
            element={<TeacherWarrantyProtected />}
          />
          {/* Teacher help / documentation (#620). Same Clerk gate +
              dev-token bypass as the rest of the teacher tree. */}
          <Route path="/teacher/help" element={<TeacherHelpProtected />} />
          <Route
            path="/teacher/help/:categoryId"
            element={<TeacherHelpArticleProtected />}
          />
          <Route
            path="/teacher/help/:categoryId/:slug"
            element={<TeacherHelpArticleProtected />}
          />
          <Route path="/student" element={<StudentLogin />} />
          {/* PIPA P2 (#128) chunk 3 — Art. 22-2 §3 student notice
              screen. Reached via the SplashScreen login handler
              when the chunk-2 backend reports notice_required, and
              by the StudentLogin mount-time refresh when a
              mid-session POLICY_VERSION bump triggers the same
              flag. The page POSTs to
              /api/auth/student-acknowledge-notice on the
              "이해했어요" button and navigates back to /student. */}
          <Route path="/student/notice" element={<StudentNotice />} />
          {/* Issue #967 (PR-3) — student first-login set-password
              screen. Reached via the SplashScreen login handler when the
              PR-2 backend reports ``password_setup_required`` on a
              legacy-bootstrap login, and by the StudentLogin mount-time
              refresh when ``/student-me`` reports the same flag (a
              passwordless student who deep-linked to the picker). The
              page POSTs to /api/auth/student-set-password and navigates
              to /student on success. */}
          <Route
            path="/student/set-password"
            element={<StudentSetPassword />}
          />
          <Route
            path="/student/settings"
            element={<StudentAccountSettings />}
          />
          <Route
            path="/student/session/:sessionId"
            element={<TutoringSession />}
          />
          {/* Student help / documentation (#620). No frontend gate —
              matches the rest of the student tree; backend bearer
              checks happen at the API boundary. */}
          <Route path="/student/help" element={<StudentHelp />} />
          <Route
            path="/student/help/:categoryId"
            element={<HelpArticle role="student" />}
          />
          <Route
            path="/student/help/:categoryId/:slug"
            element={<HelpArticle role="student" />}
          />

          {/* English — /en prefix. */}
          <Route path="/en" element={<MarketingHome />} />
          <Route path="/en/teacher" element={<TeacherListProtected />} />
          <Route
            path="/en/teacher/class/:classCode"
            element={<TeacherDashboardProtected />}
          />
          <Route
            path="/en/teacher/class/:classCode/materials"
            element={<ClassMaterialsProtected />}
          />
          <Route
            path="/en/teacher/settings"
            element={<TeacherSettingsProtected />}
          />
          <Route
            path="/en/teacher/warranty"
            element={<TeacherWarrantyProtected />}
          />
          {/* English duals for the teacher help routes (#620). */}
          <Route path="/en/teacher/help" element={<TeacherHelpProtected />} />
          <Route
            path="/en/teacher/help/:categoryId"
            element={<TeacherHelpArticleProtected />}
          />
          <Route
            path="/en/teacher/help/:categoryId/:slug"
            element={<TeacherHelpArticleProtected />}
          />
          <Route path="/en/student" element={<StudentLogin />} />
          <Route path="/en/student/notice" element={<StudentNotice />} />
          <Route
            path="/en/student/set-password"
            element={<StudentSetPassword />}
          />
          <Route
            path="/en/student/settings"
            element={<StudentAccountSettings />}
          />
          <Route
            path="/en/student/session/:sessionId"
            element={<TutoringSession />}
          />
          {/* English duals for the student help routes (#620). */}
          <Route path="/en/student/help" element={<StudentHelp />} />
          <Route
            path="/en/student/help/:categoryId"
            element={<HelpArticle role="student" />}
          />
          <Route
            path="/en/student/help/:categoryId/:slug"
            element={<HelpArticle role="student" />}
          />

          {/* Anything else: back to the Korean splash. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        </RouteErrorBoundary>
        {/* Global UI chrome — the switchers render on every page,
            including admin and Clerk routes, so users can flip
            language + theme from anywhere. */}
        <LanguageSwitcher />
        <ThemeSwitcher />
        <PrivacyFooter />
        <CookieNotice />
        <ProviderSwitcher />
        {/* Revocation banner (#306 surface 5). Listens for the
            ``api:auth-revoked`` event from ``api/client.ts`` and
            surfaces a "reload to see the latest state" prompt
            when the backend returns 401/403 without the existing
            auto-recovery flows claiming it. */}
        <RevocationBanner />
      </SiteGate>
      </TestingModeGate>
    </ThemeProvider>
  )
}

export default App
